import { useIntl } from 'react-intl';

import { PresetKey, PresetProps } from './types';

export function usePreset(presetKey: PresetKey): PresetProps {
  const intl = useIntl();

  switch (presetKey) {
    case 'new-clause':
      return {
        description: intl.formatMessage({
          id: 'EmptyPage.newClause.description',
          defaultMessage:
            'Tag a clause in your contract and let our AI find similar ones, making them searchable, trackable, and analyzable.',
          description: 'Description for the new clause empty page',
        }),
        title: intl.formatMessage({
          id: 'EmptyPage.newClause.title',
          defaultMessage: 'Automate clause identification',
          description: 'Title for the new clause empty page',
        }),
        image: 'empty-no-groups' as const,
        imageAlt: 'no clauses',
      };
    case 'no-alerts':
      return {
        description: intl.formatMessage({
          id: 'EmptyPage.noAlerts.description',
          defaultMessage:
            'You have not added any alerts and no alerts have been shared with you.',
          description: 'Description for the no alerts empty page',
        }),
        title: intl.formatMessage({
          id: 'EmptyPage.noAlerts.title',
          defaultMessage: 'You don’t have any alerts.',
          description: 'Title for the no alerts empty page',
        }),
        image: 'empty-no-alerts' as const,
        imageAlt: 'no alerts',
      };
    case 'no-clause-selected':
      return {
        description: intl.formatMessage({
          id: 'EmptyPage.noClauseSelected.description',
          defaultMessage: 'Select a Clause on the left to view content.',
          description:
            'Message shown when no clause is selected in the clause viewer.',
        }),
        title: intl.formatMessage({
          id: 'EmptyPage.noClauseSelected.title',
          defaultMessage: 'No Clause Selected.',
          description: 'Title for the no clause selected empty page',
        }),
        image: undefined,
        imageAlt: '',
      };
    case 'no-clause-variations-admin':
      return {
        description: intl.formatMessage({
          id: 'EmptyPage.noClauseVariationsAdmin.description',
          defaultMessage:
            'Continue building your library using the + button to add Clause Variations.',
          description: 'Message for admins when a clause has no variations.',
        }),
        title: intl.formatMessage({
          id: 'EmptyPage.noClauseVariationsAdmin.title',
          defaultMessage: 'Selected Clause has no variations.',
          description: 'Title for the no clause variations (admin) empty page',
        }),
        image: undefined,
        imageAlt: '',
      };
    case 'no-clause-variations-user':
      return {
        description: intl.formatMessage({
          id: 'EmptyPage.noClauseVariationsUser.description',
          defaultMessage:
            'Please contact your Administrator to continue building your library of standardized language.',
          description:
            'Message for users when a clause has no variations and they are not an admin.',
        }),
        title: intl.formatMessage({
          id: 'EmptyPage.noClauseVariationsUser.title',
          defaultMessage: 'Selected Clause has no variations.',
          description: 'Title for the no clause variations (user) empty page',
        }),
        image: undefined,
        imageAlt: '',
      };
    case 'no-clauses-admin':
      return {
        description: intl.formatMessage({
          id: 'EmptyPage.noClausesAdmin.description',
          defaultMessage:
            'Start building your library by adding Clauses and Clause Variations.',
          description:
            'Message for admins when there are no clauses in the library.',
        }),
        title: intl.formatMessage({
          id: 'EmptyPage.noClausesAdmin.title',
          defaultMessage: 'No Clauses to display.',
          description: 'Title for the no clauses (admin) empty page',
        }),
        image: undefined,
        imageAlt: '',
      };
    case 'no-clauses-user':
      return {
        description: intl.formatMessage({
          id: 'EmptyPage.noClausesUser.description',
          defaultMessage:
            'Please contact your Administrator to start building a library of standardized language.',
          description:
            'Message for users when there are no clauses and they are not an admin.',
        }),
        title: intl.formatMessage({
          id: 'EmptyPage.noClausesUser.title',
          defaultMessage: 'No Clauses to display.',
          description: 'Title for the no clauses (user) empty page',
        }),
        image: undefined,
        imageAlt: '',
      };
    case 'no-dashboard':
      return {
        description: intl.formatMessage({
          id: 'EmptyPage.noDashboard.description',
          defaultMessage: 'Explore custom dashboards or build your own.',
          description: 'Message shown when no dashboard is found.',
        }),
        title: intl.formatMessage({
          id: 'EmptyPage.noDashboard.title',
          defaultMessage: 'Dashboard not found.',
          description: 'Title for the no dashboard empty page',
        }),
        image: 'empty-no-search' as const,
        imageAlt: 'no dashboard',
      };
    case 'no-documents':
      return {
        description: intl.formatMessage({
          id: 'EmptyPage.noDocuments.description',
          defaultMessage:
            'Once documents are uploaded, they will be displayed here.',
          description:
            'Message shown when there are no documents in the folder.',
        }),
        title: intl.formatMessage({
          id: 'EmptyPage.noDocuments.title',
          defaultMessage: 'This folder is empty.',
          description: 'Title for the no documents empty page',
        }),
        image: 'empty-no-documents' as const,
        imageAlt: 'no documents',
      };
    case 'no-groups':
      return {
        description: intl.formatMessage({
          id: 'EmptyPage.noGroups.description',
          defaultMessage:
            'Groups are an easy way to keep track of related documents. Link documents to each other, define relationships and keep track of supporting documents.',
          description: 'Message shown when there are no groups created yet.',
        }),
        title: intl.formatMessage({
          id: 'EmptyPage.noGroups.title',
          defaultMessage: 'There are no groups yet.',
          description: 'Title for the no groups empty page',
        }),
        image: 'empty-no-groups' as const,
        imageAlt: 'no groups',
      };
    case 'no-matches':
      return {
        description: intl.formatMessage({
          id: 'EmptyPage.noMatches.description',
          defaultMessage: 'Try adjusting your search term or filters.',
          description: 'Message shown when a search yields no results.',
        }),
        title: intl.formatMessage({
          id: 'EmptyPage.noMatches.title',
          defaultMessage: 'No matches.',
          description: 'Title for the no matches empty page',
        }),
        image: undefined,
        imageAlt: '',
      };
    case 'no-playbook-selected':
      return {
        description: intl.formatMessage({
          id: 'EmptyPage.noPlaybookSelected.description',
          defaultMessage: 'Select a Playbook on the left to view content.',
          description:
            'Message shown when no playbook is selected in the playbook viewer.',
        }),
        title: intl.formatMessage({
          id: 'EmptyPage.noPlaybookSelected.title',
          defaultMessage: 'No Playbook Selected.',
          description: 'Title for the no playbook selected empty page',
        }),
        image: undefined,
        imageAlt: '',
      };
    case 'no-guideline-selected':
      return {
        description: intl.formatMessage({
          id: 'EmptyPage.noGuidelineSelected.description',
          defaultMessage: 'Select a Guideline on the left to view content.',
          description:
            'Message shown when no guideline is selected in the guideline viewer.',
        }),
        title: intl.formatMessage({
          id: 'EmptyPage.noGuidelineSelected.title',
          defaultMessage: 'No Guideline Selected.',
          description: 'Title for the no guideline selected empty page',
        }),
        image: undefined,
        imageAlt: '',
      };
    case 'no-search':
      return {
        description: intl.formatMessage({
          id: 'EmptyPage.noSearch.description',
          defaultMessage: 'Please enter your search criteria to see results.',
          description: 'Message shown before a search is performed.',
        }),
        title: intl.formatMessage({
          id: 'EmptyPage.noSearch.title',
          defaultMessage: 'Please enter search criteria.',
          description: 'Title for the no search empty page',
        }),
        image: 'empty-no-search' as const,
        imageAlt: 'no search',
      };
    case 'no-saved-dashboards':
      return {
        description: '',
        title: intl.formatMessage({
          id: 'EmptyPage.noSavedDashboards.title',
          defaultMessage: 'No Saved Dashboards',
          description: 'Title for the no saved dashboards empty page',
        }),
        image: 'empty-saved-dashboards' as const,
        imageAlt: 'no saved dashboards',
      };
    case 'no-selected-library-models':
      return {
        description: intl.formatMessage({
          id: 'EmptyPage.noSelectedLibraryModels.description',
          defaultMessage: 'Select models from the library to add to your cart.',
          description: 'Message shown when no library models are selected.',
        }),
        title: intl.formatMessage({
          id: 'EmptyPage.noSelectedLibraryModels.title',
          defaultMessage: 'No models selected.',
          description: 'Title for the no selected library models empty page',
        }),
        image: undefined,
        imageAlt: '',
      };
    case 'no-version-match':
      return {
        description: intl.formatMessage({
          id: 'EmptyPage.noVersionMatch.description',
          defaultMessage: 'Select another version or document to view',
          description:
            'Message shown when the selected version does not exist.',
        }),
        title: intl.formatMessage({
          id: 'EmptyPage.noVersionMatch.title',
          defaultMessage: "This version doesn't exist",
          description: 'Title for the no version match empty page',
        }),
        image: 'empty-version' as const,
        imageAlt: 'version not found',
      };
    case 'no-webhooks':
      return {
        description: intl.formatMessage({
          id: 'EmptyPage.noWebhooks.description',
          defaultMessage:
            'Create webhook endpoints to listen for Evisort events, allowing your integration to receive notifications when asynchronous actions take place.',
          description:
            'Message shown when there are no webhook endpoints created.',
        }),
        title: intl.formatMessage({
          id: 'EmptyPage.noWebhooks.title',
          defaultMessage: 'Create Endpoints for Evisort Events',
          description: 'Title for the no webhooks empty page',
        }),
        image: undefined,
        imageAlt: '',
      };
    case 'no-uploaded-files':
      return {
        description: intl.formatMessage({
          id: 'EmptyPage.noUploadedFiles.description',
          defaultMessage: 'You haven’t uploaded any documents yet…',
          description:
            'Message shown when the user has not uploaded any files.',
        }),
        title: intl.formatMessage({
          id: 'EmptyPage.noUploadedFiles.title',
          defaultMessage: 'No documents uploaded yet.',
          description: 'Title for the no uploaded files empty page',
        }),
        image: undefined,
        imageAlt: '',
      };
    default:
      return {
        description: '',
        title: '',
        image: undefined,
        imageAlt: '',
      };
  }
}
